import { useEffect, useMemo, useState } from 'react'
import { fetchAdminConfig, saveAdminConfig, fetchRoutineTasks, saveRoutineTasks, fetchMembers, importGoalsCsv, importWorklogsCsv } from '../utils/api'
import type { AdminConfig, MemberProfile, RoutineTask, TeamMember } from '../types/okr'

const defaultCron = {
  weekly: '0 15 * * 5',
  monthly: '0 18 28-31 * *'
}

const buildCron = (r: Partial<RoutineTask>) => {
  const minute = Number.isFinite(r.minute) ? Number(r.minute) : 0
  const hour = Number.isFinite(r.hour) ? Number(r.hour) : 9
  const dayOfWeek = Number.isFinite(r.dayOfWeek) ? Number(r.dayOfWeek) : 1 // 1=周一
  const dayOfMonth = Number.isFinite(r.dayOfMonth) ? Number(r.dayOfMonth) : 1
  const type = r.scheduleType || 'weekly'
  if (type === 'weekly') return `${minute} ${hour} * * ${dayOfWeek}`
  if (type === 'monthly') return `${minute} ${hour} ${dayOfMonth} * *`
  if (type === 'quarterly') return `${minute} ${hour} ${dayOfMonth} 1,4,7,10 *`
  if (type === 'halfyear') return `${minute} ${hour} ${dayOfMonth} 1,7 *`
  return r.cron || '0 9 * * 1'
}

const emptyRoutine = (): RoutineTask => {
  const base: RoutineTask = {
    id: crypto.randomUUID(),
    title: '',
    description: '',
    scheduleType: 'weekly',
    dayOfWeek: 1,
    dayOfMonth: 1,
    hour: 9,
    minute: 0,
    cron: '0 9 * * 1',
    assigneeId: '',
    assigneeName: '',
    channel: 'wxwork',
    enabled: true
  }
  base.cron = buildCron(base)
  return base
}

const defaultTeam = {
  department: '信息管理部',
  owner: { name: '于建波', userId: 'GS2480', dept: [] as number[] },
  members: [{ name: '周影', userId: 'GS3818', dept: [] as number[] }]
}

function Admin() {
  const [config, setConfig] = useState<AdminConfig | null>(null)
  const [routines, setRoutines] = useState<RoutineTask[]>([])
  const [members, setMembers] = useState<MemberProfile[]>([])
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [membersError, setMembersError] = useState('')
  const [membersSyncedAt, setMembersSyncedAt] = useState<string>('')
  const [goalsCsv, setGoalsCsv] = useState('')
  const [worklogsCsv, setWorklogsCsv] = useState('')
  const [importing, setImporting] = useState(false)

  const ownerId = useMemo(() => localStorage.getItem('okr_userId') || '', [])
  const team = useMemo<{ department: string; owner: TeamMember; members: TeamMember[] }>(() => {
    const base = (config?.team || {}) as Partial<NonNullable<AdminConfig['team']>>
    const owner = { ...defaultTeam.owner, ...(base.owner || {}) }
    const memberList = Array.isArray(base.members) && base.members.length > 0 ? base.members : defaultTeam.members
    const normalizedMembers = memberList
      .map((m: TeamMember) => ({ name: m.name || m.userId || '', userId: m.userId || '', dept: m.dept || [] as number[] }))
      .filter((m) => m.userId)
    return {
      department: base.department || defaultTeam.department,
      owner: owner.userId ? owner as TeamMember : defaultTeam.owner,
      members: normalizedMembers
    }
  }, [config])
  const memberOptions = useMemo(() => {
    const fallback = [team.owner, ...(team.members || [])]
      .filter(Boolean)
      .map((m) => ({ userId: m.userId, name: m.name || m.userId, dept: m.dept || [] as number[] }))
    const merged = [...members, ...fallback].filter((m) => m && m.userId)
    const seen = new Set<string>()
    return merged.filter((m) => {
      const key = m.userId.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [members, team])

  useEffect(() => {
    const load = async () => {
      try {
        const [cfg, rts] = await Promise.all([
          fetchAdminConfig(),
          fetchRoutineTasks()
        ]) as [AdminConfig, RoutineTask[]]
        const baseCfg: AdminConfig = cfg || { ownerId: defaultTeam.owner.userId, yuque: { token: '', repoPath: '', weeklyCron: '', monthlyCron: '' }, notify: { enableWxwork: true }, routines: [] }
        const safeCfg: AdminConfig = {
          ...baseCfg,
          team: baseCfg.team || defaultTeam,
          ownerId: baseCfg.team?.owner?.userId || baseCfg.ownerId || defaultTeam.owner.userId
        }
        setConfig(safeCfg)
        setRoutines(rts || [])
      } catch (error) {
        console.error(error)
        alert('加载后台配置失败')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const syncMembers = async () => {
    setLoadingMembers(true)
    setMembersError('')
    try {
      const list = await fetchMembers()
      setMembers(list)
      setMembersSyncedAt(new Date().toISOString())
    } catch (error) {
      console.error(error)
      setMembersError('通讯录同步失败，请确认企微权限或稍后再试')
    } finally {
      setLoadingMembers(false)
    }
  }

  useEffect(() => {
    syncMembers()
  }, [])

  const updateConfig = (patch: Partial<AdminConfig>) => {
    setConfig((prev) => (prev ? { ...prev, ...patch } : prev))
  }

  const updateYuque = (patch: Partial<AdminConfig['yuque']>) => {
    setConfig((prev) => (prev ? { ...prev, yuque: { ...prev.yuque, ...patch } } : prev))
  }

  const updateTeam = (patch: Partial<NonNullable<AdminConfig['team']>>) => {
    setConfig((prev) => (prev ? { ...prev, team: { ...defaultTeam, ...(prev.team || defaultTeam), ...patch } } : prev))
  }

  const updateTeamOwner = (patch: Partial<TeamMember>) => {
    setConfig((prev) => {
      if (!prev) return prev
      const team = { ...defaultTeam, ...(prev.team || defaultTeam), owner: { ...defaultTeam.owner, ...(prev.team?.owner || {}), ...patch } }
      return { ...prev, team, ownerId: team.owner.userId || prev.ownerId }
    })
  }

  const updateTeamMembers = (next: TeamMember[]) => {
    setConfig((prev) => (prev ? { ...prev, team: { ...defaultTeam, ...(prev.team || defaultTeam), members: next } } : prev))
  }

  const handleSave = async () => {
    if (!config) return
    setSaving(true)
    try {
      const payload: AdminConfig = {
        ...config,
        ownerId: config.team?.owner?.userId || config.ownerId,
        team: { ...defaultTeam, ...(config.team || defaultTeam) }
      }
      await saveAdminConfig(payload)
      await saveRoutineTasks(routines)
      setConfig(payload)
      alert('已保存后台配置与例行任务')
    } catch (error) {
      console.error(error)
      alert('保存失败，请重试')
    } finally {
      setSaving(false)
    }
  }

  const handleImportGoals = async () => {
    if (!goalsCsv.trim()) return alert('请粘贴 CSV 内容')
    setImporting(true)
    try {
      await importGoalsCsv(goalsCsv)
      alert('目标 CSV 已导入')
    } catch (error) {
      console.error(error)
      alert('导入失败，请检查格式或字段')
    } finally {
      setImporting(false)
    }
  }

  const handleImportWorklogs = async () => {
    if (!worklogsCsv.trim()) return alert('请粘贴 CSV 内容')
    setImporting(true)
    try {
      await importWorklogsCsv(worklogsCsv)
      alert('工作日志 CSV 已导入')
    } catch (error) {
      console.error(error)
      alert('导入失败，请检查格式或字段')
    } finally {
      setImporting(false)
    }
  }

  const addTeamMember = () => {
    const list = config?.team?.members || []
    updateTeamMembers([...list, { name: '', userId: '' }])
  }

  const updateTeamMember = (index: number, patch: Partial<TeamMember>) => {
    const list = config?.team?.members || []
    const next = list.map((m, i) => (i === index ? { ...m, ...patch } : m))
    updateTeamMembers(next)
  }

  const removeTeamMember = (index: number) => {
    const list = config?.team?.members || []
    const next = list.filter((_, i) => i !== index)
    updateTeamMembers(next)
  }

  const addRoutine = () => setRoutines((prev) => [...prev, emptyRoutine()])
  const updateRoutine = (id: string, patch: Partial<RoutineTask>) => {
    setRoutines((prev) => prev.map((r) => {
      if (r.id !== id) return r
      const next = { ...r, ...patch }
      // 自动刷新 CRON
      next.cron = buildCron(next)
      return next
    }))
  }
  const removeRoutine = (id: string) => setRoutines((prev) => prev.filter((r) => r.id !== id))

  if (loading) return <div className="page-shell">后台配置加载中...</div>
  if (!config) return <div className="page-shell">暂无后台配置</div>

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <p className="section-kicker">后台管理 · 仅 Owner 可见</p>
          <h2>系统设置与自动化</h2>
          <p className="muted">当前登录：{ownerId || '未知用户'}</p>
        </div>
        <button className="primary-btn" onClick={handleSave} disabled={saving}>
          {saving ? '保存中...' : '保存全部'}
        </button>
      </div>

      <section className="card">
        <p className="section-kicker">团队配置</p>
        <h3>手工维护团队与负责人</h3>
        <div className="form-grid">
          <label>
            <span>部门名称</span>
            <input
              value={team.department}
              onChange={(e) => updateTeam({ department: e.target.value })}
              placeholder="如：信息管理部"
            />
          </label>
          <label>
            <span>负责人姓名</span>
            <input
              value={team.owner?.name || ''}
              onChange={(e) => updateTeamOwner({ name: e.target.value })}
              placeholder="如：于建波"
            />
          </label>
          <label>
            <span>负责人ID（userId）</span>
            <input
              value={team.owner?.userId || ''}
              onChange={(e) => updateTeamOwner({ userId: e.target.value })}
              placeholder="如：GS2480"
            />
            <small>保存后系统权限将以此 ID 为 Owner 判定，成员只可见本人日志。</small>
          </label>
        </div>
        <div className="panel-header">
          <h4>团队成员</h4>
          <button className="secondary-btn" onClick={addTeamMember}>添加成员</button>
        </div>
        {team.members.length === 0 && <div className="muted">暂无成员，请添加</div>}
        {team.members.length > 0 && (
          <div className="routine-list">
            {team.members.map((m, idx) => (
              <div className="routine-item" key={`${m.userId || 'member'}-${idx}`}>
                <div className="routine-grid">
                  <label>
                    <span>成员姓名</span>
                    <input
                      value={m.name}
                      onChange={(e) => updateTeamMember(idx, { name: e.target.value })}
                      placeholder="如：周影"
                    />
                  </label>
                  <label>
                    <span>成员ID</span>
                    <input
                      value={m.userId}
                      onChange={(e) => updateTeamMember(idx, { userId: e.target.value })}
                      placeholder="如：GS3818"
                    />
                  </label>
                  <button className="text-btn" onClick={() => removeTeamMember(idx)}>移除</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="helper-text">权限规则：仅负责人可见全员日志和后台，成员仅可见/编辑自己的日志与个人报告。</p>
      </section>

      <div className="card-grid two">
        <section className="card">
          <p className="section-kicker">语雀集成</p>
          <h3>报表落库配置</h3>
          <label>
            <span>知识库路径</span>
            <input
              value={config.yuque.repoPath}
              onChange={(e) => updateYuque({ repoPath: e.target.value })}
              placeholder="如：space/repo"
            />
          </label>
          <label>
            <span>API Token</span>
            <input
              value={config.yuque.token}
              onChange={(e) => updateYuque({ token: e.target.value })}
              placeholder="语雀 Token"
            />
          </label>
          <label>
            <span>周报 Cron</span>
            <input
              value={config.yuque.weeklyCron || defaultCron.weekly}
              onChange={(e) => updateYuque({ weeklyCron: e.target.value })}
            />
            <small>默认：每周五 15:00 → 0 15 * * 5</small>
          </label>
          <label>
            <span>月报 Cron</span>
            <input
              value={config.yuque.monthlyCron || defaultCron.monthly}
              onChange={(e) => updateYuque({ monthlyCron: e.target.value })}
            />
            <small>默认：每月 28-31 日 18:00 → 0 18 28-31 * *</small>
          </label>
          <label className="inline">
            <input
              type="checkbox"
              checked={config.notify.enableWxwork}
              onChange={(e) => updateConfig({ notify: { enableWxwork: e.target.checked } })}
            />
            <span>生成后推送企微消息</span>
          </label>
          <p className="helper-text">报表按成员分章节落库：周报-姓名-YYYY-MM-DD / 月报-姓名-YYYY-MM</p>
        </section>

        <section className="card">
          <p className="section-kicker">例行工作提醒</p>
          <h3>周期任务</h3>
          <button className="secondary-btn" onClick={addRoutine}>新增例行任务</button>
          <div className="routine-list">
            {routines.length === 0 && <div className="muted">暂无例行任务</div>}
            {routines.map((r) => (
              <div className="routine-item" key={r.id}>
                <div className="routine-header">
                  <input
                    value={r.title}
                    onChange={(e) => updateRoutine(r.id, { title: e.target.value })}
                    placeholder="任务标题"
                  />
                  <div className="inline-actions">
                    <span className="helper-text">{r.enabled ? '启用' : '停用'}</span>
                    <button className="text-btn" onClick={() => removeRoutine(r.id)}>
                      删除
                    </button>
                  </div>
                </div>
                <textarea
                  rows={2}
                  value={r.description}
                  onChange={(e) => updateRoutine(r.id, { description: e.target.value })}
                  placeholder="描述"
                />
                <div className="routine-grid">
                  <label>
                    <span>周期类型</span>
                    <select
                      value={r.scheduleType || 'weekly'}
                      onChange={(e) => updateRoutine(r.id, { scheduleType: e.target.value as RoutineTask['scheduleType'] })}
                    >
                      <option value="weekly">周度</option>
                      <option value="monthly">月度</option>
                      <option value="quarterly">季度</option>
                      <option value="halfyear">半年</option>
                    </select>
                  </label>
                  {r.scheduleType === 'weekly' && (
                    <>
                      <label>
                        <span>每周星期</span>
                        <select value={r.dayOfWeek ?? 1} onChange={(e) => updateRoutine(r.id, { dayOfWeek: Number(e.target.value) })}>
                          {[0,1,2,3,4,5,6].map((d) => <option key={d} value={d}>周{['日','一','二','三','四','五','六'][d]}</option>)}
                        </select>
                      </label>
                    </>
                  )}
                  {r.scheduleType !== 'weekly' && (
                    <label>
                      <span>每月/季度/半年 第几天</span>
                      <input type="number" min={1} max={31} value={r.dayOfMonth ?? 1} onChange={(e) => updateRoutine(r.id, { dayOfMonth: Number(e.target.value) })} />
                    </label>
                  )}
                  <label>
                    <span>小时</span>
                    <input type="number" min={0} max={23} value={r.hour ?? 9} onChange={(e) => updateRoutine(r.id, { hour: Number(e.target.value) })} />
                  </label>
                  <label>
                    <span>分钟</span>
                    <input type="number" min={0} max={59} value={r.minute ?? 0} onChange={(e) => updateRoutine(r.id, { minute: Number(e.target.value) })} />
                  </label>
                  <label>
                    <span>生成的 CRON</span>
                    <input
                      value={r.cron}
                      onChange={(e) => updateRoutine(r.id, { cron: e.target.value })}
                      placeholder="如 0 15 * * 5"
                    />
                    <small>可按需微调；修改上方时间会自动更新。</small>
                  </label>
                  {memberOptions.length > 0 ? (
                    <label className="full-span">
                      <span>责任人（企微通讯录 / 团队配置）</span>
                      <select
                        value={r.assigneeId}
                        onChange={(e) => {
                          const uid = e.target.value
                          const profile = memberOptions.find((m) => m.userId === uid)
                          updateRoutine(r.id, { assigneeId: uid, assigneeName: profile?.name || '' })
                        }}
                      >
                        <option value="">请选择</option>
                        {memberOptions.map((m) => <option key={m.userId} value={m.userId}>{m.name}（{m.userId}）</option>)}
                      </select>
                      {r.assigneeName && <div className="helper-text">已选：{r.assigneeName}</div>}
                    </label>
                  ) : (
                    <>
                      <label>
                        <span>责任人ID</span>
                        <input
                          value={r.assigneeId}
                          onChange={(e) => updateRoutine(r.id, { assigneeId: e.target.value })}
                          placeholder="企微 userId"
                        />
                      </label>
                      <label>
                        <span>责任人姓名</span>
                        <input
                          value={r.assigneeName}
                          onChange={(e) => updateRoutine(r.id, { assigneeName: e.target.value })}
                          placeholder="展示用"
                        />
                      </label>
                    </>
                  )}
                  <label className="inline">
                    <input
                      type="checkbox"
                      checked={r.enabled}
                      onChange={(e) => updateRoutine(r.id, { enabled: e.target.checked })}
                    />
                    <span>启用提醒</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="card-grid two">
        <section className="card">
          <p className="section-kicker">通讯录</p>
          <div className="panel-header">
            <h3>团队成员（仅负责人可同步企微）</h3>
            <div className="action-group">
              {membersSyncedAt && <span className="helper-text">上次同步：{new Date(membersSyncedAt).toLocaleString()}</span>}
              <button className="secondary-btn" onClick={syncMembers} disabled={loadingMembers}>{loadingMembers ? '同步中...' : '刷新通讯录'}</button>
            </div>
          </div>
          {membersError && <div className="error-text">{membersError}</div>}
          {loadingMembers && <div className="muted">同步通讯录中...</div>}
          {!loadingMembers && members.length === 0 && <div className="muted">暂无成员数据，请确认企微接口权限</div>}
          <ul className="bullet-list">
            {members.slice(0, 30).map((m) => (
              <li key={m.userId}>{m.name} · {m.userId} {m.dept?.length ? `· 部门 ${m.dept.join('/')}` : ''}</li>
            ))}
          </ul>
          {members.length > 30 && <div className="helper-text">已截断显示 30 人，完整名单用于责任人选择、例行任务和报表生成。</div>}
        </section>

        <section className="card">
          <p className="section-kicker">数据导入</p>
          <h3>CSV 快速导入</h3>
          <label>
            <span>导入执行目标（必含 level,period,title,owner,status,progress）</span>
            <textarea rows={4} value={goalsCsv} onChange={(e) => setGoalsCsv(e.target.value)} placeholder="header 行后每行一条，用逗号分隔" />
          </label>
          <button className="secondary-btn" onClick={handleImportGoals} disabled={importing}>{importing ? '导入中...' : '导入目标 CSV'}</button>
          <label>
            <span>导入工作日志（必含 date,title,project,ownerName,timeSpent）</span>
            <textarea rows={4} value={worklogsCsv} onChange={(e) => setWorklogsCsv(e.target.value)} placeholder="header 行后每行一条，用逗号分隔" />
          </label>
          <button className="secondary-btn" onClick={handleImportWorklogs} disabled={importing}>{importing ? '导入中...' : '导入日志 CSV'}</button>
          <p className="helper-text">建议使用 UTF-8 CSV，字段可含 tags、blockers、nextStep、dueDate 等补充信息。</p>
        </section>
      </div>
    </div>
  )
}

export default Admin
